/**
 * CascadeControls — shared hierarchy-cascade toolbar for a per-tenant
 * settings domain ('models', 'modules' or 'meta_agents').
 *
 * Renders, for the selected tenant:
 *   - a "differs from parent" badge (semantic drift vs the parent tenant),
 *   - a "managed by parent" notice when an ancestor enforces this domain,
 *   - an "Apply to sub-tenants" button (dry-run count → confirm → apply),
 *   - an "Enforce on sub-tenants" switch (enabling re-applies subtree-wide;
 *     any direct edit on a descendant auto-clears it).
 *
 * Backend endpoints (domain-parameterized):
 *   POST  /super-admin/tenants/<id>/{model-families|features|meta-agent-models}/apply-to-descendants
 *   PATCH /super-admin/tenants/<id>/{model-families|features|meta-agent-models}/enforcement
 *
 * The parent panel owns the GET that produces `cascade` and passes a
 * refresh callback so badges update after any cascade action.
 */

import { useState } from 'react';
import { Button, Switch, Tag, Tooltip, Modal, message as antdMessage } from 'antd';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import { ModelChangeImpact, ModelImpact, impactIsEmpty } from './ModelChangeImpact';

export interface CascadeStatus {
  enforced: boolean;
  has_children: boolean;
  differs_from_parent: boolean;
  parent_enforced: boolean;
  parent: { tenant_id: string; tenant_name: string } | null;
}

interface CascadeControlsProps {
  tenantId: string;
  domain: 'models' | 'modules' | 'meta_agents';
  cascade: CascadeStatus | null;
  /** Called after any successful cascade action so the parent can refetch. */
  onChanged: () => void;
}

const DOMAIN_PATH: Record<CascadeControlsProps['domain'], string> = {
  models: 'model-families',
  modules: 'features',
  meta_agents: 'meta-agent-models',
};

export function CascadeControls({ tenantId, domain, cascade, onChanged }: CascadeControlsProps) {
  const { t } = useLanguage();
  const [applying, setApplying] = useState(false);
  const [togglingEnforce, setTogglingEnforce] = useState(false);

  if (!cascade) return null;

  const path = DOMAIN_PATH[domain];
  const parentName = cascade.parent?.tenant_name || '';

  async function applyToDescendants() {
    setApplying(true);
    try {
      // Dry-run first: show the admin exactly which tenants will be written.
      const dry = await api.post(
        `/super-admin/tenants/${tenantId}/${path}/apply-to-descendants`,
        { dry_run: true }
      );
      const count: number = dry.data?.count ?? 0;
      const targets: Array<{ tenant_name: string }> = dry.data?.targets || [];
      // Models: who the copy moves to another release (item 92), the same
      // check the tab runs before its own saves.
      const impact: ModelImpact | null = dry.data?.impact || null;
      const blocking = (impact?.blocking || 0) > 0;
      if (count === 0) {
        antdMessage.info(t('superAdmin.cascade.noDescendants') || 'This tenant has no sub-tenants.');
        return;
      }
      Modal.confirm({
        title: t('superAdmin.cascade.applyConfirmTitle') || 'Apply to all sub-tenants?',
        content: (
          <div>
            <div style={{ marginBottom: 8 }}>
              {(t('superAdmin.cascade.applyConfirmBody') ||
                'These settings will overwrite the same settings on {count} sub-tenant(s):')
                .replace('{count}', String(count))}
            </div>
            <div style={{ maxHeight: 160, overflowY: 'auto', fontSize: 12, opacity: 0.8 }}>
              {targets.map((tg, i) => <div key={i}>• {tg.tenant_name}</div>)}
            </div>
            {!impactIsEmpty(impact) && (
              <div style={{ marginTop: 12, maxHeight: 320, overflowY: 'auto' }}>
                <ModelChangeImpact impact={impact as ModelImpact} />
              </div>
            )}
          </div>
        ),
        width: impactIsEmpty(impact) ? undefined : 620,
        okText: blocking ? t('superAdmin.modelImpact.saveAnyway') : (t('superAdmin.cascade.applyConfirmOk') || 'Apply'),
        okButtonProps: blocking ? { danger: true } : undefined,
        cancelText: t('common.cancel') || 'Cancel',
        onOk: async () => {
          // The surrounding try/catch has already exited by the time onOk
          // runs — errors must be surfaced here or the apply fails silently.
          try {
            const res = await api.post(
              `/super-admin/tenants/${tenantId}/${path}/apply-to-descendants`,
              blocking ? { confirm_breaking: true } : {}
            );
            antdMessage.success(
              (t('superAdmin.cascade.applied') || 'Applied to {count} sub-tenant(s).')
                .replace('{count}', String(res.data?.count ?? count))
            );
            onChanged();
          } catch (err: any) {
            antdMessage.error(err?.response?.data?.error || `${err?.message || err}`);
            throw err; // keep the modal's OK-button error state consistent
          }
        },
      });
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error || `${err?.message || err}`);
    } finally {
      setApplying(false);
    }
  }

  async function toggleEnforcement(enforced: boolean) {
    setTogglingEnforce(true);
    try {
      const res = await api.patch(
        `/super-admin/tenants/${tenantId}/${path}/enforcement`,
        { enforced }
      );
      if (enforced) {
        antdMessage.success(
          (t('superAdmin.cascade.enforceOn') ||
            'Enforcement on — settings pushed to {count} sub-tenant(s). Direct edits on a sub-tenant will turn this off.')
            .replace('{count}', String(res.data?.descendants_updated ?? 0))
        );
      } else {
        antdMessage.success(t('superAdmin.cascade.enforceOff') || 'Enforcement off — sub-tenants may now diverge.');
      }
      onChanged();
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error || `${err?.message || err}`);
    } finally {
      setTogglingEnforce(false);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        padding: '8px 12px',
        background: 'var(--bg-tertiary, rgba(148,163,184,0.08))',
        borderRadius: 8,
      }}
    >
      {/* Drift + parent-enforcement badges */}
      {cascade.parent_enforced && (
        <Tooltip
          title={(t('superAdmin.cascade.parentEnforcedTip') ||
            'The parent tenant enforces these settings on its subtree. Editing here will break its enforcement.')
          }
        >
          <Tag color="gold" style={{ margin: 0 }}>
            {(t('superAdmin.cascade.parentEnforced') || 'Enforced by {parent}')
              .replace('{parent}', parentName)}
          </Tag>
        </Tooltip>
      )}
      {cascade.parent && cascade.differs_from_parent && (
        <Tooltip
          title={(t('superAdmin.cascade.differsTip') ||
            'This tenant\'s settings differ from its parent tenant\'s settings.')}
        >
          <Tag color="blue" style={{ margin: 0 }}>
            {(t('superAdmin.cascade.differs') || 'Differs from {parent}')
              .replace('{parent}', parentName)}
          </Tag>
        </Tooltip>
      )}
      {cascade.parent && !cascade.differs_from_parent && (
        <Tag style={{ margin: 0, opacity: 0.7 }}>
          {(t('superAdmin.cascade.matchesParent') || 'Matches {parent}')
            .replace('{parent}', parentName)}
        </Tag>
      )}

      {/* Subtree actions — only meaningful when there ARE sub-tenants */}
      {cascade.has_children && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginInlineStart: 'auto', flexWrap: 'wrap' }}>
          <Button size="small" onClick={applyToDescendants} loading={applying}>
            {t('superAdmin.cascade.applyButton') || 'Apply to sub-tenants'}
          </Button>
          <Tooltip
            title={t('superAdmin.cascade.enforceTip') ||
              'While on, every edit here is pushed to all sub-tenants. Editing a sub-tenant directly turns this off.'}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Switch
                size="small"
                checked={cascade.enforced}
                loading={togglingEnforce}
                onChange={toggleEnforcement}
              />
              <span style={{ fontSize: 12 }}>
                {t('superAdmin.cascade.enforceLabel') || 'Enforce on sub-tenants'}
              </span>
            </span>
          </Tooltip>
        </div>
      )}
    </div>
  );
}
